import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/axios';
import { API_ROUTES, ATTRIBUTE_TRANSLATIONS } from '@/constants';
import {
  Card,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
} from '@/components/ui';
import { Input } from '@/components/forms';
import { Paginator } from '@/components/ui/Paginator/Paginator';
import { useModalStore } from '@/stores';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';
import { InfoModal } from '@/components/modals/InfoModal';
import styles from './Providers.module.css';

const PAGE_SIZE = 10;

interface Attribute {
  id: string;
  name: string;
  slug: string;
  input_type: string;
  options: string | null;
}

export function Attributes() {
  const { openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: allData, isLoading } = useQuery<Attribute[]>({
    queryKey: ['attributes'],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.attributes);
      return Array.isArray(response.data) ? response.data : (response.data.results ?? []);
    },
  });

  const filteredData = useMemo(() => {
    if (!allData) return [];
    if (!search.trim()) return allData;
    const s = search.toLowerCase();
    return allData.filter((attr) => attr.name.toLowerCase().includes(s));
  }, [allData, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.attributes}${id}/`);
    },
    onSuccess: () => {
      toast.success('Attribute deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['attributes'] });
      closeModal();
    },
    onError: () => toast.error('Error deleting attribute'),
  });

  const handleDelete = (attribute: Attribute) => {
    openModal(
      <DeleteConfirmationModal
        title="Delete Attribute"
        itemName={attribute.name}
        onConfirm={() => deleteMutation.mutate(attribute.id)}
        isDeleting={deleteMutation.isPending}
      />,
    );
  };

  return (
    <div className={styles.container}>
      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <Button
            variant="primary"
            onClick={() =>
              openModal(
                <InfoModal
                  title="Coming Soon"
                  message="The functionality to add attributes will be available soon."
                />,
              )
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Attribute
          </Button>
        </div>

        <div className={styles.filters}>
          <Input
            name="search"
            label="Search"
            placeholder="Search attributes by name..."
            value={search}
            setValue={handleSearch}
            variant="bordered"
            isClearable
          />
        </div>

        <Table aria-label="Attributes list" radius={8}>
          <TableHeader>
            <TableColumn>Name</TableColumn>
            <TableColumn>Type</TableColumn>
            <TableColumn>Options</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody isLoading={isLoading}>
            {paginatedData.map((attribute) => (
              <TableRow key={attribute.id}>
                <TableCell>{ATTRIBUTE_TRANSLATIONS[attribute.name] || attribute.name}</TableCell>
                <TableCell>{attribute.input_type}</TableCell>
                <TableCell>{attribute.options ?? '-'}</TableCell>
                <TableCell>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(attribute)}>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Paginator page={page} numPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
